import React, { Fragment, useEffect } from 'react';
import { MenuItem, Select, TextField, FormControl, InputLabel } from '@mui/material';
import { AlertCircle } from 'react-feather';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import FilterAccordian from '../filterAccordian';
import Breadcrumb from '../common/breadcrumb';
import { userRequest } from '../../auth/requestMethods';
import { CalendarMonth } from '@mui/icons-material';
import { Modal, ModalHeader, ModalBody, Alert } from 'reactstrap';
import { toast } from 'react-toastify';
import CustomTable from '../tables/MuiDataGrid';
import * as Yup from 'yup';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import dateFormat from '../../utils/dateFormat';

const Manage = ({ history }) => {
  useEffect(() => {
    document.title = 'PFSA | Public Holidays';
  }, []);
  const currentYear = new Date();

  const [perPage, setPerPage] = React.useState(10);
  const [page, setPage] = React.useState(1);
  const [dataIsFetching, setDataIsFetching] = React.useState(true);

  const [tableData, setTableData] = React.useState({ count: 0, holidays: [] });

  const [filterOptions, setFilterOptions] = React.useState({
    years: [currentYear.getFullYear() + 1, currentYear.getFullYear()],
  });
  const [searchInput, setSearchInput] = React.useState({ year: 'All' });
  const [duplicate, setDuplicate] = React.useState(true);

  const [copyInputs, setCopyInputs] = React.useState({
    from: currentYear.getFullYear(),
    to: currentYear.getFullYear() + 1,
  });

  const [modalState, setModalState] = React.useState({
    update: false,
    open: false,
  });

  const [copyModalState, setCopyModalState] = React.useState(false);

  const minmax = {
    min:
      searchInput.year && searchInput.year !== 'All'
        ? new Date(`${searchInput.year}-01-01`).toISOString().slice(0, 10)
        : '1900-01-01',
    max:
      searchInput.year && searchInput.year !== 'All'
        ? new Date(`${searchInput.year}-12-31`).toISOString().slice(0, 10)
        : '2100-12-31',
  };

  const {
    register,
    handleSubmit,
    watch,
    reset,
    control,
    trigger,
    clearErrors,
    getValues,
    formState: { errors, isSubmitted },
  } = useForm({
    resolver: yupResolver(validationSchema(minmax.min, minmax.max)),
    defaultValues,
  });

  React.useEffect(() => {
    const subscription = watch((value, { name }) => trigger(name));
    return () => subscription.unsubscribe();
  }, [watch, trigger]);

  React.useEffect(() => {
    setDataIsFetching(true);
    bulkAxios(['/public-holidays/years'])
      .then(async (all) => {
        setFilterOptions((pr) => ({
          years: [...new Set([...pr.years.concat(all[0].years.map((y) => y.year))])],
        }));

        try {
          let { data } = await userRequest().get(
            `/public-holidays?per_page=${perPage}&page=${page}&${buildQuery(null)}`
          );
          console.log(data);
          setTableData({
            count: data.total,
            holidays: data.publicHolidays,
          });
        } catch (error) {
          console.log(error);
        }
        setDataIsFetching(false);
      })
      .catch((error) => {
        console.log(error);
        setDataIsFetching(false);
      });
  }, [perPage, page, searchInput]);

  function bulkAxios(urls = null) {
    if (!urls) return urls;

    let promises = urls.map((req) =>
      userRequest()
        .get(req)
        .then(({ data }) => {
          return data;
        })
        .catch((error) => {
          console.log(error);
          return null;
        })
    );

    return Promise.all(promises);
  }

  function meetFilterConditions(data, isUpdate = true) {
    if (!isUpdate) {
      if (tableData.count === perPage) return false;
    }
    return true;
  }

  const onSubmit = (data) => {
    toast.info(`${modalState.update ? 'Updating...' : 'Creating...'}`, {
      isLoading: true,
      position: 'bottom-center',
      autoClose: 5000,
      theme: 'colored',
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      toastId: 'create',
    });
    let api = modalState.update ? `/public-holidays/update/${modalState.update}` : '/public-holidays/';
    userRequest()
      .post(api, data)
      .then((res) => {
        toast.dismiss('create');
        toast.success(`Successfully ${modalState.update ? 'updated' : 'created'}`, {
          position: 'bottom-center',
          autoClose: 5000,
          closeOnClick: true,
          draggable: true,
        });
        const holiday = res.data.publicHoliday;
        let holidays = [];
        if (modalState.update) {
          holidays = tableData.holidays.map((s) => {
            if (s.id === holiday.id) return holiday;
            else return s;
          });
        } else {
          holidays = meetFilterConditions(holiday, false) ? [...tableData.holidays, holiday] : tableData.holidays;
        }
        setTableData((prev) => ({
          holidays,
          count: modalState.update ? prev.count : prev.count + 1,
        }));
        setModalState({ update: false, open: false });
        reset(defaultValues, {});
      })
      .catch(({ response }) => {
        toast.dismiss('create');
        toast.error(response.data.message, {
          position: 'bottom-center',
          autoClose: 5000,
          closeOnClick: true,
          draggable: true,
        });
      });
  };

  function buildQuery(foundFilter = null, vals) {
    return Object.entries(vals ? vals : searchInput)
      .map(([key, value]) => {
        if (value !== '' && value !== 'All') {
          if (foundFilter) foundFilter.push(key);
          return encodeURIComponent(key) + '=' + encodeURIComponent(value);
        }
        return '';
      })
      .filter((Q) => !!Q)
      .join('&');
  }

  async function checkExistingEntries(year) {
    try {
      const { data } = await userRequest().get(`/public-holidays?per_page=1&page=1&year=${year}`);
      setDuplicate(!!data.total);
    } catch {}
  }

  const onSearch = (e) => {
    e.preventDefault();
    const queryString = buildQuery();
    userRequest()
      .get(`/public-holidays?per_page=${perPage}&page=${page}&${queryString}`)
      .then(({ data }) => {
        setTableData({
          count: data.total,
          holidays: data.publicHolidays,
        });
      })
      .catch(({ response }) => {
        console.log(response);
      });
  };
  // if (value !== "") {
  //   else if (key === "year") {
  //     if(value === All){
  //       return [];
  //     }else{
  //     return `Year: ${value}`;}
  //   }
  // }
  function getFilters() {
    return Object.entries(searchInput)
      .map(([key, value]) => {
        if (value !== '') {
          if (key === 'year') {
            if (value === 'All') {
              return [];
            } else {
              return `Year: ${value}`;
            }
          }
        }
      })
      .filter((x) => x !== undefined && x.length !== 0);
  }

  const onChangeInput = (e, val, name = null) => {
    if (name) setSearchInput({ ...searchInput, [name]: val });
    else setSearchInput({ ...searchInput, [e.target.name]: e.target.value });
  };

  const onChangeCopy = (e) => {
    setCopyInputs({ ...copyInputs, [e.target.name]: e.target.value });
  };

  const copyYear = async () => {
    toast.info(`Copying...`, {
      isLoading: true,
      position: 'bottom-center',
      autoClose: 5000,
      theme: 'colored',
      closeOnClick: true,
      draggable: true,
      toastId: 'copy',
    });
    userRequest()
      .post(`/public-holidays/copy`, { from: copyInputs.from, to: copyInputs.to })
      .then(({ data }) => {
        console.log(data);
        toast.dismiss('copy');
        toast.success(data?.message || 'Success', {
          position: 'bottom-center',
          autoClose: 5000,
          closeOnClick: true,
          draggable: true,
        });
      })
      .catch(({ response }) => {
        console.log(response);
        toast.dismiss('copy');
        toast.error(response.data?.message || 'Something Went wrong', {
          position: 'bottom-center',
          autoClose: 5000,
          closeOnClick: true,
          draggable: true,
        });
      });
  };

  const actionMethods = {
    toggleEdit: (row) => {
      setModalState({ open: true, update: row.id });
      reset(row, {});
    },
    toggleDelete: (row) => {
      // request delete
      // row.id
      userRequest()
        .post(`/public-holidays/delete/${row.id}`)
        .then(({ data }) =>
          setTableData((prev) => ({
            count: prev.count - 1,
            holidays: prev.holidays.filter((h) => h.id !== row.id),
          }))
        )
        .catch((error) => console.log(error));
    },
  };

  const columns = [
    {
      field: 'date',
      headerName: 'Date',
      flex: 1,
      valueFormatter: ({ value }) => dateFormat(new Date(value?.slice(0, 10))),
    },
    {
      field: 'display_name',
      headerName: 'Name',
      flex: 1,
    },
  ];

  return (
    <Fragment>
      <Breadcrumb icon={<CalendarMonth />} title="Manage" parent="Public Holidays" />

      <div className="container-fluid">
        <FilterAccordian filters={getFilters()}>
          <form className="theme-form" noValidate="" onSubmit={onSearch}>
            <div className="form-group row mx-0">
              <FormControl>
                <InputLabel id="demo-simple-select-helper-label">Year</InputLabel>
                <Select
                  name="year"
                  value={searchInput.year}
                  onChange={onChangeInput}
                  labelId="demo-simple-select-helper-label"
                  id="demo-simple-select-helper"
                  label="Year"
                  style={{ fontStyle: searchInput.year === 'All' ? 'italic' : 'normal' }}
                >
                  <MenuItem value={'All'} style={{ fontStyle: 'italic' }}>
                    All
                  </MenuItem>
                  {filterOptions.years.map((y, i) => (
                    <MenuItem key={i} value={y}>
                      {y}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </form>
        </FilterAccordian>
      </div>
      {!tableData.count && (
        <Alert className="m-15 d-flex gap-3" color="secondary dark">
          <AlertCircle />
          <p>No public holidays have been specified for this year yet</p>
        </Alert>
      )}
      <div className="form-group d-flex gap-4 flex-wrap">
        <button
          onClick={() => {
            setModalState({ update: false, open: true });
            clearErrors();
          }}
          className="btn btn-secondary m-15"
          type="submit"
        >
          <i className="fa fa-plus fa-xl" aria-hidden="true"></i>
        </button>
        <button
          onClick={() => {
            checkExistingEntries(copyInputs.to);
            setCopyModalState(true);
          }}
          className="btn btn-secondary m-15"
          type="button"
        >
          Copy from other year
        </button>
      </div>
      <Modal
        isOpen={modalState.open}
        toggle={() => {
          setModalState({ update: false, open: false });
          reset(defaultValues, {});
        }}
        size="lg"
        scrollable={true}
        centered={true}
      >
        <ModalHeader
          toggle={() => {
            setModalState({ update: false, open: false });
            reset(defaultValues, {});
          }}
        >
          {modalState.update ? 'Edit' : 'Create'} Holiday
        </ModalHeader>
        <ModalBody>
          <div className="card-body">
            <form className="theme-form" onSubmit={handleSubmit(onSubmit)}>
              <div className="form-group row col-4">
                <LocalizationProvider LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Controller
                    control={control}
                    name="date"
                    render={({ field }) => (
                      <DatePicker
                        views={['month', 'day']}
                        inputFormat="dd/MM/yyyy"
                        name={field.name}
                        value={field.value}
                        onBlur={field.onBlur}
                        onChange={(val) => field.onChange(val)}
                        minDate={minmax.min}
                        label="Date"
                        renderInput={(params) => (
                          <TextField
                            sx={{
                              '& .css-o9k5xi-MuiInputBase-root-MuiOutlinedInput-root': {
                                width: '310%',
                              },
                            }}
                            {...params}
                            onKeyDown={(e) => e.preventDefault()}
                            error={!!(errors.date?.message && isSubmitted && getValues().date === '')}
                            // helperText={errors.date?.message && isSubmitted ? errors.date.message : ''}
                          />
                        )}
                      />
                    )}
                  />
                </LocalizationProvider>
              </div>
              <div className="form-group row">
                <TextField
                  label="Description"
                  variant="outlined"
                  {...register(`display_name`)}
                  error={!!(errors.display_name?.message && isSubmitted && getValues().display_name === '')}
                />
              </div>
              <div>
                <button className="btn btn-primary me-1" type="submit">
                  {modalState.update ? 'Save' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </ModalBody>
      </Modal>
      {/* //////////////////////////////////// COPY MODAL ///////////////////////////// */}
      <Modal
        isOpen={copyModalState}
        toggle={() => setCopyModalState(false)}
        size="lg"
        scrollable={true}
        centered={true}
      >
        <ModalHeader toggle={() => setCopyModalState(false)}>Copy Public Holidays</ModalHeader>
        <ModalBody>
          <div className="card-body">
            <form className="theme-form" onSubmit={handleSubmit(onSubmit)}>
              <div className="form-group row">
                <Alert className=" d-flex gap-3" color="secondary dark">
                  <AlertCircle />
                  <p>Please remember to check public holidays after copied, since some dates may vary year to year</p>
                </Alert>
              </div>
              <div className="form-group row">
                <FormControl>
                  <InputLabel id="demo-simple-select-helper-label">Copy from Year</InputLabel>
                  <Select
                    name="from"
                    value={copyInputs.from}
                    onChange={onChangeCopy}
                    labelId="demo-simple-select-helper-label"
                    id="demo-simple-select-helper"
                    label="Copy from Year"
                  >
                    {filterOptions.years.slice(1).map((y) => (
                      <MenuItem key={y} value={y}>
                        {y}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              <div className="form-group row">
                <FormControl>
                  <InputLabel id="demo-simple-select-helper-label">Copy to Year</InputLabel>
                  <Select
                    name="to"
                    value={copyInputs.to}
                    onChange={(e) => {
                      checkExistingEntries(e.target.value);
                      onChangeCopy(e);
                    }}
                    labelId="demo-simple-select-helper-label"
                    id="demo-simple-select-helper"
                    label="Copy to Year"
                  >
                    {filterOptions.years
                      .filter((x) => x !== copyInputs.from)
                      .map((y) => (
                        <MenuItem key={y} value={y}>
                          {y}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </div>
              {/* if data exist for to year */}
              {duplicate && (
                <div className="form-group row">
                  <Alert className=" d-flex gap-3" color="warning dark">
                    <AlertCircle />
                    <p>The year you are copyng to already contains Public Holidays - duplicates may be created</p>
                  </Alert>
                </div>
              )}
              <div>
                <button className="btn btn-primary me-1" onClick={copyYear}>
                  Copy
                </button>
              </div>
            </form>
          </div>
        </ModalBody>
      </Modal>
      <div className="container-fluid">
        <CustomTable
          rows={tableData.holidays ? tableData.holidays : []}
          actions={actionMethods}
          columns={columns}
          setPageSize={setPerPage}
          setPage={setPage}
          rowCount={tableData.count}
          pageSize={perPage}
          loading={dataIsFetching}
          sortModel={[{ field: 'date', sort: 'asc' }]}
        />
      </div>
    </Fragment>
  );
};

const defaultValues = { date: new Date().toISOString().slice(0, 10), display_name: '' };

const validationSchema = (minDate, maxDate) => {
  return Yup.object().shape({
    date: Yup.date('Undefined date').required('Required'),
    display_name: Yup.string().required('Required'),
  });
};

export default Manage;
